import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type StudioStateChangeCandidate = {
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  country: string | null
  address: string | null
  status_code: string | null
  owner_partner_number: number | null
  owner_persons_fio: string
  owner_start_date: string | null
  owner_phase: string | null
  active_offer_count: number
  active_offer_price: number | null
  active_offer_currency: string | null
  active_offer_is_bargain_allowed: boolean | null
}

export type StudioCancellationCandidate = {
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  country: string | null
  address: string | null
  status_code: string | null
  owner_partner_number: number | null
  owner_persons_fio: string
  owner_start_date: string | null
  owner_phase: string | null
  ownership_created_at: string
}

export type StudioCancellationPartnerCandidate = {
  partner_number: number
  persons_fio: string
  planned_studio_count: number
  first_planned_ownership_created_at: string | null
  last_planned_ownership_created_at: string | null
  cities_text: string
}

export type CreateStudioSaleOfferResult = {
  studio_internal_id: number
  studio_number: number | null
  ownership_id: number
  partner_number: number
  old_phase: string
  new_phase: 'FOR_SALE'
  offer_id: number
  offer_date: string
  price: number
  currency: string | null
  is_bargain_allowed: boolean
  closed_offers: number
  changed_at: string
}

export type UpdateStudioSaleOfferResult = {
  studio_internal_id: number
  studio_number: string
  previous_active_offer_date: string
  new_offer_id: number
  offer_date: string
  changed_at: string
  price: number
  currency: string | null
  is_bargain_allowed: boolean
  closed_offers_count: number
}

export type CancelStudioSaleOfferResult = {
  studio_internal_id: number
  studio_number: string
  changed_at: string
  closed_offers_count: number
  ownership_id: number | null
  old_phase: string | null
  new_phase: string | null
  phase_changed: boolean
}

export type SetStudioServiceStatusResult = {
  studio_internal_id: number
  studio_number: number | null
  old_status: string | null
  new_status: 'SERVICE'
  changed_at: string
}

export type ResumeStudioResult = {
  studio_internal_id: number
  studio_number: string
  old_status: string | null
  new_status: 'OPEN'
  changed_at: string
}

export type SuspendStudioResult = {
  studio_internal_id: number
  studio_number: number
  old_status: string | null
  new_status: 'SUSPENDED'
  changed_at: string
  changed_by: string | null
}

export type StudioClosureType = 'STANDARD' | 'REBRANDING' | 'UNAPPROVED'

export type CloseStudioResult = {
  studio_internal_id: number
  studio_number: string
  old_status: string | null
  new_status: 'CLOSED'
  ownership_id: number
  old_phase: string | null
  new_phase: 'CLOSED'
  partner_number: number
  closure_type: StudioClosureType
  changed_at: string
  active_offers_count: number
  closed_offers_count: number
  groups_closed_count: number
}

export type ChangeStudioAddressResult = {
  studio_internal_id: number
  studio_number: string
  city_name: string | null
  old_address: string | null
  new_address: string
  changed_at: string
  changed_by: string | null
  updated_count: number
}

export type CancelStudioResult = {
  studio_internal_id: number
  studio_number: number | null
  old_status: string | null
  new_status: 'CANCELLED'
  ownership_id: number
  old_phase: string | null
  new_phase: 'NOT_OPENED'
  partner_number: number
  created_at_from?: string
  changed_at: string
}

export type TransferStudioToManagementResult = {
  studio_internal_id: number
  studio_number: string
  old_ownership_id: number
  old_partner_number: number
  old_phase: string | null
  new_partner_number: number
  new_series_id: number
  new_ownership_id: number
  changed_at: string
  for_sale_at: string
  active_offers_count: number
  closed_offers_count: number
  new_offer_id: number
}

export async function fetchStudioStateChangeCandidates(): Promise<StudioStateChangeCandidate[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_state_change_candidates')
  )) as { data: StudioStateChangeCandidate[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchStudioAddressChangeCandidates(): Promise<StudioStateChangeCandidate[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_address_change_candidates')
  )) as { data: StudioStateChangeCandidate[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchStudioCancellationCandidates(): Promise<StudioCancellationCandidate[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_cancellation_candidates')
  )) as { data: StudioCancellationCandidate[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchStudioCancellationPartnerCandidates(): Promise<StudioCancellationPartnerCandidate[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_cancellation_partner_candidates')
  )) as { data: StudioCancellationPartnerCandidate[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function createStudioSaleOffer(params: {
  studioInternalId: number
  price: number
  isBargainAllowed: boolean
  currency?: string | null
  changedAt?: string | null
  note?: string | null
}): Promise<CreateStudioSaleOfferResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('create_studio_sale_offer', {
      p_studio_internal_id: params.studioInternalId,
      p_price: params.price,
      p_is_bargain_allowed: params.isBargainAllowed,
      p_currency: params.currency || null,
      p_changed_at: params.changedAt || null,
      p_note: params.note?.trim() || null,
    })
  )) as { data: CreateStudioSaleOfferResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не выставлена на продажу')
  return data
}

export async function updateStudioSaleOfferFromOriginalCase(params: {
  studioNumber: string
  price: number
  isBargainAllowed: boolean
  currency?: string | null
  changedAt: string
}): Promise<UpdateStudioSaleOfferResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('update_studio_sale_offer_from_original_case', {
      v_studio_number: params.studioNumber,
      v_changed_at: params.changedAt,
      v_new_price: params.price,
      v_new_is_bargain_allowed: params.isBargainAllowed,
      v_currency: params.currency || null,
    })
  )) as { data: UpdateStudioSaleOfferResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Оффер студии не изменен')
  return data
}

export async function cancelStudioSaleOfferFromOriginalCase(params: {
  studioNumber: string
  changedAt: string
}): Promise<CancelStudioSaleOfferResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('cancel_studio_sale_offer_from_original_case', {
      v_studio_number: params.studioNumber,
      v_changed_at: params.changedAt,
    })
  )) as { data: CancelStudioSaleOfferResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Оффер студии не закрыт')
  return data
}

export async function setStudioServiceStatus(params: {
  studioInternalId: number
  changedAt?: string | null
  note?: string | null
}): Promise<SetStudioServiceStatusResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_studio_service_status', {
      p_studio_internal_id: params.studioInternalId,
      p_changed_at: params.changedAt || null,
      p_note: params.note?.trim() || null,
    })
  )) as { data: SetStudioServiceStatusResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не переведена в статус нет продаж')
  return data
}

export async function resumeStudioFromOriginalCase(params: {
  studioNumber: string
  changedAt: string
}): Promise<ResumeStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('resume_studio_from_original_case', {
      v_studio_number: params.studioNumber,
      v_my_ts: params.changedAt,
    })
  )) as { data: ResumeStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не возобновила работу')
  return data
}

export async function suspendStudioFromOriginalCase(params: {
  studioNumber: number
  changedAt: string
}): Promise<SuspendStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('suspend_studio_from_original_case', {
      v_studio_number: params.studioNumber,
      v_my_ts: params.changedAt,
    })
  )) as { data: SuspendStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не приостановлена')
  return data
}

export async function closeStudioFromOriginalCase(params: {
  studioNumber: string
  changedAt: string
  closureType: StudioClosureType
}): Promise<CloseStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('close_studio_from_original_case', {
      v_studio_number: params.studioNumber,
      v_my_ts: params.changedAt,
      v_closure_type: params.closureType,
    })
  )) as { data: CloseStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не закрыта')
  return data
}

export async function changeStudioAddressFromOriginalCase(params: {
  studioNumber: string
  newAddress: string
  changedAt: string
}): Promise<ChangeStudioAddressResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('change_studio_address_from_original_case', {
      v_studio_number: params.studioNumber,
      v_new_address: params.newAddress.trim(),
      v_user: null,
      v_now: params.changedAt,
    })
  )) as { data: ChangeStudioAddressResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Адрес студии не изменен')
  return data
}

export async function cancelStudioByIdFromOriginalCase(params: {
  studioInternalId: number
  changedAt: string
}): Promise<CancelStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('cancel_studio_by_id_from_original_case', {
      v_target_studio_id: params.studioInternalId,
      v_my_ts: params.changedAt,
    })
  )) as { data: CancelStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не отменена')
  return data
}

export async function cancelStudioByPartnerFromOriginalCase(params: {
  partnerNumber: number
  createdAtFrom: string
  changedAt: string
}): Promise<CancelStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('cancel_studio_by_partner_from_original_case', {
      v_partner_number: params.partnerNumber,
      v_created_at_from: params.createdAtFrom,
      v_my_ts: params.changedAt,
    })
  )) as { data: CancelStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия франчайзи не отменена')
  return data
}

export async function transferStudioToManagementFromOriginalCase(params: {
  studioNumber: string
  changedAt: string
  managementPartnerNumber?: number
}): Promise<TransferStudioToManagementResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('transfer_studio_to_management_from_original_case', {
      v_studio_number: params.studioNumber,
      v_now: params.changedAt,
      v_new_partner_number: params.managementPartnerNumber ?? 1000,
    })
  )) as { data: TransferStudioToManagementResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не передана в УК')
  return data
}
